/**
 * Shared constants for the AI search module: API base URL/endpoints,
 * suggestion chips/cards seed content, fallback image, and keyboard
 * shortcut key bindings.
 */
// The one backend setting the whole app uses; a separate AI-only URL once leaked localhost from .env into production builds.
const _rawBase = process.env.REACT_APP_API_BASE || "";

export const API_BASE = _rawBase.endsWith("/api") ? _rawBase : _rawBase.replace(/\/$/, "") + "/api";

if (process.env.NODE_ENV === "development") {
    console.log("[API] Using endpoint:", API_BASE);
}

export const ENDPOINTS = {
  AI_ASK:   `${API_BASE}/ai/ask`,
  AI_MORE:  `${API_BASE}/ai/more`,
  AREA_LOOKUP: `${API_BASE}/ai/area-lookup`,

  PERSONAL_AGENT_START:     `${API_BASE}/ai/personal-agent/start`,
  PERSONAL_AGENT_ANSWER:    `${API_BASE}/ai/personal-agent/answer`,
  PERSONAL_AGENT_CONFIRM:   `${API_BASE}/ai/personal-agent/confirm`,
  PERSONAL_AGENT_CORRECT:   `${API_BASE}/ai/personal-agent/correct`,
  PERSONAL_AGENT_REMOVE_PREFILLED: `${API_BASE}/ai/personal-agent/remove-prefilled`,
  PERSONAL_AGENT_SUBSCRIBE: `${API_BASE}/ai/personal-agent/subscribe`,
  PERSONAL_AGENT_STATUS:    `${API_BASE}/ai/personal-agent/status`,
  PERSONAL_AGENT_PAUSE:  `${API_BASE}/ai/personal-agent/pause`,
  PERSONAL_AGENT_RESUME: `${API_BASE}/ai/personal-agent/resume`,
  PERSONAL_AGENT_RESET:  `${API_BASE}/ai/personal-agent/reset`,
  PERSONAL_AGENT_MATCHES:`${API_BASE}/ai/personal-agent/matches`,
  PERSONAL_AGENT_ENGAGEMENT: `${API_BASE}/ai/personal-agent/engagement`,
  PERSONAL_AGENT_SEARCH_NOW: `${API_BASE}/ai/personal-agent/search-now`,
  PERSONAL_AGENT_APPLY_RELAXATION: `${API_BASE}/ai/personal-agent/apply-relaxation`,
};

// ── "Talk to Me" entry point (homepage floating pill -> /ai-search) ──
//
// The pill is only an entry point: it authenticates (when needed) and
// navigates to AiSearchPage, which stays the sole owner of the chat
// session and of the six-question guided onboarding. The questions
// themselves live in the backend's personal_agent stage machine and are
// surfaced turn-by-turn through /api/ai/ask (responses flagged
// isInlineOnboarding) -- nothing about them is duplicated on the client.
export const AI_SEARCH_ROUTE = "/ai-search";

/** Value passed as router navigation state so AiSearchPage can tell this
 *  entry apart from a free-form question typed elsewhere. */
export const TALK_TO_ME_ENTRY_POINT = "talk-to-me";

/** The opening turn that triggers the EXISTING inline onboarding on a
 *  brand-new chat. The backend starts the flow (a Yes/No gate, then the six
 *  questions) when a new chat's first property-search message carries no
 *  concrete criteria of its own (orchestrator/nodes/search.py::
 *  _is_vague_property_query) -- deliberately criteria-free so nothing is
 *  inferred instead of asked. The welcome message shown for this is the
 *  backend's own response (search.py::ONBOARDING_WELCOME_MESSAGE), not
 *  anything rendered client-side. */
export const GUIDED_ONBOARDING_SEED_QUESTION = "I'm looking for a property";

/** Shown on every new chat before anything is sent; the first message is then sent with onboardingGateShown so the
 *  backend takes it as this gate's answer. Same text as search.py::ONBOARDING_WELCOME_MESSAGE. */
export const ONBOARDING_WELCOME_MESSAGE =
    "House hunting is a tedious process. Hire me for free and let me do that for you. "
    + "Before we shortlist the properties, can I ask you a few questions?";

export const onboardingGateMessage = () => ({
    id: "onboarding-gate",
    role: "ai",
    text: ONBOARDING_WELCOME_MESSAGE,
    isInlineOnboarding: true,
    onboardingOptions: ["Yes", "No"],
    onboardingMultiSelect: false,
});

export const FALLBACK_IMAGE ="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23f1f3f4'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='16' fill='%23aab4c4'%3ENo Image%3C/text%3E%3C/svg%3E";


export const DEFAULT_SUGGESTIONS = [
  {
    label: "4 BHK Villa in Gattahalli",
    value: "tell me about The Singapore Residences in Gattahalli",
    icon:  "🏡",
    category: "property",
  },
  {
    label: "2-3 BHK in Kasavanahalli",
    value: "show me apartments in Kasavanahalli near HSR layout",
    icon:  "🏠",
    category: "location",
  },
  {
    label: "2 BHK near Wipro Gate",
    value: "2 bhk apartments near old wipro gate junnasandra",
    icon:  "💻",
    category: "location",
  },
  {
    label: "Sobha Dream Acres 2 BHK",
    value: "tell me about Sobha Dream Acres in Varthur",
    icon:  "🌿",
    category: "property",
  },
  {
    label: "Holiday villa in Tamil Nadu",
    value: "show me hello bali holiday homes",
    icon:  "🌴",
    category: "lifestyle",
  },
  {
    label: "2-4 BHK off Sarjapur Road",
    value: "apartments near sarjapur attibele road",
    icon:  "📍",
    category: "location",
  },
  {
    label: "Villa plots in Hosur Road",
    value: "villas on sarjapur bagalur road hosur",
    icon:  "🌱",
    category: "investment",
  },
  {
    label: "Luxury villa Electronic City",
    value: "luxury villa in electronic city bangalore",
    icon:  "⚡",
    category: "type",
  },
];


export const SUGGESTION_CARDS = [
  {
    title:    "Singapore Residences",
    subtitle: "₹6.32 Cr · 4 BHK Villa · Gattahalli, Bengaluru",
    query:    "tell me about The Singapore Residences in Gattahalli",
    icon:     "🏡",
    tag:      "Premium Villa",
    tagColor: "#f97316",
  },
  {
    title:    "Kasavanahalli Heights",
    subtitle: "₹1.25 Cr · 2-3 BHK · Near HSR Layout",
    query:    "apartments in Kasavanahalli Heights near HSR layout",
    icon:     "🏠",
    tag:      "Popular",
    tagColor: "#7c3aed",
  },
  {
    title:    "Junnasandra Enclave",
    subtitle: "₹40 L · 2-3 BHK · Near Old Wipro Gate",
    query:    "2 bhk apartments near old wipro gate junnasandra",
    icon:     "💻",
    tag:      "IT Hub",
    tagColor: "#2563eb",
  },
  {
    title:    "Sobha Dream Acres",
    subtitle: "₹75.5 L · 2 BHK · Varthur, Bengaluru",
    query:    "Sobha Dream Acres 2 bhk in Varthur",
    icon:     "🌿",
    tag:      "Gated Community",
    tagColor: "#16a34a",
  },
  {
    title:    "Hello Bali Holiday Homes",
    subtitle: "₹1.25 Cr · 2 BHK · Nariyanahalli, Tamil Nadu",
    query:    "hello bali holiday homes resort villa",
    icon:     "🌴",
    tag:      "Weekend Retreat",
    tagColor: "#0891b2",
  },
];

export const KEYBOARD_SHORTCUTS = {
  NEW_CHAT:       "n",
  SEARCH:         "k",
  TOGGLE_SIDEBAR: "b",
};

/** Stage 2's area-shape editor (AreaShapeEditor.jsx) -- matches the
 *  backend's own MAX_AREA_SHAPES (orchestrator/nodes/search.py). */
export const MAX_AREA_SHAPES = 5;
